import { useState, useEffect, useRef } from 'react';

interface UseCameraBackgroundResult {
  videoRef: React.RefObject<HTMLVideoElement>;
  videoError: string | null;
  streamActive: boolean;
}

// const VIDEO_ELEMENT_ID = 'ar-video-feed'; // Not used

export function useCameraBackground(enabled: boolean): UseCameraBackgroundResult {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [streamActive, setStreamActive] = useState<boolean>(false);

  useEffect(() => {
    let localStream: MediaStream | null = null;
    let isMounted = true;

    const playVideo = () => {
      if (videoRef.current) {
        videoRef.current.play().catch(e => {
            console.error('Video play failed inside playVideo:', e);
            // Optionally set an error state if play fails persistently
            if (isMounted) setVideoError(`Video playback error: ${e.message}`);
        });
      }
    };

    const startVideoStream = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        console.error('getUserMedia not supported by this browser.');
        if (isMounted) {
          setVideoError('Camera access is not supported by this browser.');
          setStreamActive(false);
        }
        return;
      }

      try {
        console.log('Requesting camera stream...');
        const constraints: MediaStreamConstraints = {
          video: { facingMode: 'environment' },
          audio: false,
        };
        localStream = await navigator.mediaDevices.getUserMedia(constraints);

        if (!isMounted) {
          localStream?.getTracks().forEach(track => track.stop());
          return;
        }

        console.log('Camera stream acquired.');
        if (isMounted) {
          setStream(localStream);
          setVideoError(null);
          setStreamActive(true);
        }

        if (videoRef.current) {
          videoRef.current.srcObject = localStream;
          // Remove previous listener if any, before adding a new one
          videoRef.current.removeEventListener('loadedmetadata', playVideo);
          videoRef.current.addEventListener('loadedmetadata', playVideo);
        }

      } catch (err: any) {
        console.error('Failed to get camera stream:', err);
        if (!isMounted) return;
        
        let errorMessage = `Failed to access camera: ${err.message}`;
        if (err.name === 'NotAllowedError') {
           errorMessage = 'Camera permission denied. Please enable camera access in your browser settings.';
        } else if (err.name === 'NotFoundError') {
            errorMessage = 'No suitable environment camera found. Trying default.';
             try {
                console.log('Falling back to default camera...');
                const fallbackConstraints: MediaStreamConstraints = { video: true, audio: false };
                localStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
                 if (!isMounted) {
                    localStream?.getTracks().forEach(track => track.stop());
                    return;
                 }
                console.log('Default camera stream acquired.');
                if (isMounted) {
                  setStream(localStream);
                  setVideoError(null);
                  setStreamActive(true);
                }
                if (videoRef.current) {
                    videoRef.current.srcObject = localStream;
                    videoRef.current.removeEventListener('loadedmetadata', playVideo);
                    videoRef.current.addEventListener('loadedmetadata', playVideo);
                }
                return;
             } catch (fallbackErr: any) {
                 console.error('Failed to get default camera stream:', fallbackErr);
                 errorMessage = `Failed to access any camera: ${fallbackErr.message}`;
             }
        } else if (err.name === 'NotReadableError') {
            errorMessage = 'Camera is already in use or a hardware error occurred.';
        }
        if (isMounted) {
          setVideoError(errorMessage);
          setStream(null);
          setStreamActive(false);
        }
      }
    };

    const stopVideoStream = () => {
      console.log('Stopping video stream tracks...');
      const currentStream = stream || localStream;
      currentStream?.getTracks().forEach(track => track.stop());
      
      if (videoRef.current) {
        videoRef.current.removeEventListener('loadedmetadata', playVideo); // Clean up listener
        videoRef.current.srcObject = null;
        if (!videoRef.current.paused) {
            videoRef.current.pause();
        }
      }
      if (isMounted) {
        setStream(null);
        setStreamActive(false);
      }
    };

    if (enabled) {
      startVideoStream();
    } else {
      stopVideoStream();
    }

    return () => {
      isMounted = false;
      console.log('Cleaning up camera stream hook (enabled effect).');
      stopVideoStream();
    };
  }, [enabled]); // stream removed from deps to avoid re-runs on stream set

   // Effect to handle video element rendering or stream changing *after* initial setup
   useEffect(() => {
    const videoEl = videoRef.current;
    const playVideoOnMetadata = () => {
        if (videoEl) {
            videoEl.play().catch(e => {
                console.error('Video play failed in stream/videoRef effect:', e);
            });
        }
    };

    if (stream && videoEl) {
      if (videoEl.srcObject !== stream) { // Only update if srcObject is different
        console.log('Assigning stream to video element (stream/videoRef effect)');
        videoEl.srcObject = stream;
        videoEl.removeEventListener('loadedmetadata', playVideoOnMetadata);
        videoEl.addEventListener('loadedmetadata', playVideoOnMetadata);
      } else if (videoEl.paused && streamActive) {
        // If srcObject is already set and correct, but video is paused and should be active
        console.log('Video was paused but stream is active, attempting to play.');
        playVideoOnMetadata(); // Try to play again
      }
    }
    return () => {
        if (videoEl) {
            videoEl.removeEventListener('loadedmetadata', playVideoOnMetadata);
        }
    }
  }, [stream, streamActive]); // videoRef removed from deps as it's stable

  return { videoRef, videoError, streamActive };
} 